/**
 * Tenant Management Component
 * Platform admin UI for creating and managing tenants
 */

import React, { useState, useEffect } from 'react';
import { getApiBaseUrl } from '../../config/environment';
import { UserManagement } from './UserManagement';


interface TenantManagementProps {
  accessToken: string;
  tenantId: string;
}

interface Tenant {
  tenantId: string;
  name: string;
  domain: string;
  plan: string;
  createdAt: string;
  billingEmail?: string;
  settings?: any;
}

export const TenantManagement: React.FC<TenantManagementProps> = ({
  accessToken,
  tenantId
}) => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  // Form state
  const [newTenantName, setNewTenantName] = useState('');
  const [newTenantDomain, setNewTenantDomain] = useState('');
  const [newTenantPlan, setNewTenantPlan] = useState('free');
  const [newTenantBillingEmail, setNewTenantBillingEmail] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Edit state
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);
  const [editName, setEditName] = useState('');
  const [editDomain, setEditDomain] = useState('');
  const [editPlan, setEditPlan] = useState('free');
  const [editBillingEmail, setEditBillingEmail] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);

  // Delete state
  const [deletingTenant, setDeletingTenant] = useState<Tenant | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [viewingUsersTenant, setViewingUsersTenant] = useState<Tenant | null>(null);

  useEffect(() => {
    loadTenants();
  }, [accessToken]);

  const loadTenants = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/api/tenant-admin/list/${tenantId}`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load tenants: ${response.statusText}`);
      }

      const data = await response.json();
      setTenants(data.organizations || []);
    } catch (err: any) {
      console.error('Error loading tenants:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateTenant = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newTenantName.trim()) {
      setCreateError('Tenant name is required');
      return;
    }

    if (!newTenantDomain.trim()) {
      setCreateError('Domain is required');
      return;
    }

    // Validate domain format (basic check)
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?\.[a-zA-Z]{2,}$/;
    if (!domainRegex.test(newTenantDomain.trim())) {
      setCreateError('Please enter a valid domain (e.g., creativescombined.com or umage.dk)');
      return;
    }

    setIsCreating(true);
    setCreateError(null);

    try {
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/api/tenant-admin/create/organization`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: newTenantName,
          domain: newTenantDomain.trim().toLowerCase(),
          plan: newTenantPlan,
          billingEmail: newTenantBillingEmail || undefined,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.message || 'Failed to create tenant');
      }

      const data = await response.json();
      console.log('✅ Tenant created:', data);

      // Reset form
      setNewTenantName('');
      setNewTenantDomain('');
      setNewTenantPlan('free');
      setNewTenantBillingEmail('');
      setShowCreateForm(false);

      // Reload tenants
      await loadTenants();
    } catch (err: any) {
      console.error('Error creating tenant:', err);
      setCreateError(err.message);
    } finally {
      setIsCreating(false);
    }
  };

  const handleEditClick = (tenant: Tenant) => {
    setEditingTenant(tenant);
    setEditName(tenant.name);
    setEditDomain(tenant.domain);
    setEditPlan(tenant.plan);
    setEditBillingEmail(tenant.billingEmail || '');
    setUpdateError(null);
  };

  const handleUpdateTenant = async () => {
    if (!editingTenant) return;

    if (!editName.trim()) {
      setUpdateError('Tenant name is required');
      return;
    }

    if (!editDomain.trim()) {
      setUpdateError('Domain is required');
      return;
    }

    // Validate domain format
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?\.[a-zA-Z]{2,}$/;
    if (!domainRegex.test(editDomain.trim())) {
      setUpdateError('Please enter a valid domain (e.g., creativescombined.com or umage.dk)');
      return;
    }

    setIsUpdating(true);
    setUpdateError(null);

    try {
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/api/tenant-admin/update/${editingTenant.tenantId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          name: editName,
          domain: editDomain.trim().toLowerCase(),
          plan: editPlan,
          billingEmail: editBillingEmail || undefined,
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update tenant');
      }

      // Success - reload tenants and close modal
      await loadTenants();
      setEditingTenant(null);
    } catch (err: any) {
      setUpdateError(err.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteClick = (tenant: Tenant) => {
    setDeletingTenant(tenant);
    setDeleteError(null);
  };

  const handleDeleteTenant = async () => {
    if (!deletingTenant) return;

    setIsDeleting(true);
    setDeleteError(null);

    try {
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/api/tenant-admin/delete/${deletingTenant.tenantId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete tenant');
      }

      // Success - reload tenants and close modal
      await loadTenants();
      setDeletingTenant(null);
    } catch (err: any) {
      setDeleteError(err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const inputCls = 'w-full px-3 py-2.5 border border-gray-300 rounded-md text-body bg-white outline-none focus:border-blue-400 focus:shadow-focus transition-[border-color,box-shadow] duration-fast placeholder:text-gray-400 disabled:bg-gray-100';
  const selectCls = `${inputCls} select-input`;
  const lbl = 'block text-label font-medium text-gray-700 mb-1';
  const secBtn = 'px-4 py-2 bg-white border border-gray-300 text-label font-medium text-gray-700 rounded-md hover:bg-gray-50 transition-colors duration-fast disabled:opacity-50';
  const priBtn = 'px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-label font-semibold rounded-md transition-colors duration-fast disabled:opacity-50';
  const danBtn = 'px-4 py-2 bg-error hover:opacity-90 text-white text-label font-semibold rounded-md transition-opacity duration-fast disabled:opacity-50';
  const smBtn  = 'px-2.5 py-1.5 text-meta font-medium rounded-md border transition-colors duration-fast';
  const thCls  = 'px-4 py-3 text-left text-meta font-semibold text-gray-500 uppercase tracking-wider';
  const tdCls  = 'px-4 py-3 text-label text-gray-700';

  if (isLoading) return <div className="py-12 text-center text-body text-gray-400">Loading tenants…</div>;

  if (error) return (
    <div className="py-12 text-center">
      <p className="text-body text-error mb-4">Error: {error}</p>
      <button type="button" onClick={loadTenants} className={secBtn}>Retry</button>
    </div>
  );

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-xl font-semibold text-gray-900">Tenant Management</h2>
        <button type="button" onClick={() => setShowCreateForm(!showCreateForm)}
          className={`${priBtn} flex items-center gap-1.5`}>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Create Tenant
        </button>
      </div>

      {/* Create Tenant Form */}
      {showCreateForm && (
        <div className="bg-surface-subtle border border-gray-200 rounded-lg p-6 mb-6">
          <h3 className="text-body font-semibold text-gray-900 mb-4">Create New Tenant</h3>
          {createError && (
            <div className="mb-4 px-4 py-3 bg-error-light text-error border border-error/30 rounded-md text-label">
              <strong>Error:</strong> {createError}
            </div>
          )}
          <form onSubmit={handleCreateTenant} className="flex flex-col gap-4">
            <div>
              <label htmlFor="tenantName" className={lbl}>Tenant Name *</label>
              <input id="tenantName" type="text" value={newTenantName}
                onChange={e => setNewTenantName(e.target.value)}
                placeholder="e.g., Acme Corporation" required disabled={isCreating} className={inputCls} />
            </div>
            <div>
              <label htmlFor="tenantDomain" className={lbl}>Domain * (Whitelist)</label>
              <input id="tenantDomain" type="text" value={newTenantDomain}
                onChange={e => setNewTenantDomain(e.target.value)}
                placeholder="e.g., creativescombined.com" required disabled={isCreating} className={inputCls} />
              <p className="mt-1 text-meta text-gray-400">Users with emails from this domain will be allowed to access this tenant</p>
            </div>
            <div>
              <label htmlFor="tenantPlan" className={lbl}>Plan</label>
              <select id="tenantPlan" value={newTenantPlan} onChange={e => setNewTenantPlan(e.target.value)} disabled={isCreating} className={selectCls}>
                <option value="free">Free</option><option value="pro">Pro</option><option value="enterprise">Enterprise</option>
              </select>
            </div>
            <div>
              <label htmlFor="billingEmail" className={lbl}>Billing Email</label>
              <input id="billingEmail" type="email" value={newTenantBillingEmail}
                onChange={e => setNewTenantBillingEmail(e.target.value)}
                placeholder="billing@example.com" disabled={isCreating} className={inputCls} />
            </div>
            <div className="flex gap-3 justify-end pt-2">
              <button type="button" onClick={() => { setShowCreateForm(false); setCreateError(null); }}
                disabled={isCreating} className={secBtn}>Cancel</button>
              <button type="submit" disabled={isCreating} className={priBtn}>{isCreating ? 'Creating…' : 'Create Tenant'}</button>
            </div>
          </form>
        </div>
      )}

      {/* Tenant List */}
      <div>
        {tenants.length === 0 ? (
          <div className="py-16 text-center">
            <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <h3 className="text-body font-semibold text-gray-700 mb-1">No Tenants Found</h3>
            <p className="text-label text-gray-400">Create your first tenant to get started.</p>
          </div>
        ) : (
          <table className="w-full bg-white rounded-lg overflow-hidden shadow-sm border border-gray-200">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['Name','Domain','Tenant ID','Plan','Created','Billing Email','Actions'].map(h => (
                  <th key={h} className={thCls}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tenants.map(tenant => (
                <tr key={tenant.tenantId} className="border-b border-gray-100 hover:bg-gray-50 transition-colors duration-fast">
                  <td className={`${tdCls} font-medium`}>{tenant.name}</td>
                  <td className={`${tdCls} font-mono text-meta`}>{tenant.domain || 'N/A'}</td>
                  <td className={`${tdCls} font-mono text-meta`}>{tenant.tenantId}</td>
                  <td className={tdCls}>
                    <span className={`px-2.5 py-0.5 rounded-full text-meta font-semibold capitalize ${
                      tenant.plan === 'enterprise' ? 'bg-blue-50 text-blue-700' : tenant.plan === 'pro' ? 'bg-teal-500/10 text-teal-600' : 'bg-gray-100 text-gray-500'
                    }`}>{tenant.plan}</span>
                  </td>
                  <td className={tdCls}>{new Date(tenant.createdAt).toLocaleDateString()}</td>
                  <td className={tdCls}>{tenant.billingEmail || '—'}</td>
                  <td className={tdCls}>
                    <div className="flex gap-2">
                      <button type="button" onClick={() => setViewingUsersTenant(tenant)} className={`${smBtn} bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100`}>View Users</button>
                      <button type="button" onClick={() => handleEditClick(tenant)} className={`${smBtn} bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100`}>Edit</button>
                      <button type="button" onClick={() => handleDeleteClick(tenant)} className={`${smBtn} bg-error-light text-error border-error/30 hover:bg-red-100`}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Edit Tenant Modal */}
      {editingTenant && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setEditingTenant(null)}>
          <div className="w-full max-w-md bg-white rounded-xl shadow-xl p-6 flex flex-col gap-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-semibold text-gray-900">Edit Tenant</h3>
            {updateError && <div className="px-4 py-3 bg-error-light text-error border border-error/30 rounded-md text-label">{updateError}</div>}
            <div>
              <label htmlFor="editTenantName" className={lbl}>Tenant Name *</label>
              <input id="editTenantName" type="text" value={editName} onChange={e => setEditName(e.target.value)} placeholder="e.g., Acme Corporation" required disabled={isUpdating} className={inputCls} />
            </div>
            <div>
              <label htmlFor="editTenantDomain" className={lbl}>Domain * (Whitelist)</label>
              <input id="editTenantDomain" type="text" value={editDomain} onChange={e => setEditDomain(e.target.value)} placeholder="e.g., creativescombined.com" required disabled={isUpdating} className={inputCls} />
              <p className="mt-1 text-meta text-gray-400">Users with emails from this domain will be allowed to access this tenant</p>
            </div>
            <div>
              <label htmlFor="editTenantPlan" className={lbl}>Plan *</label>
              <select id="editTenantPlan" value={editPlan} onChange={e => setEditPlan(e.target.value)} required disabled={isUpdating} className={selectCls}>
                <option value="free">Free</option><option value="pro">Pro</option><option value="enterprise">Enterprise</option>
              </select>
            </div>
            <div>
              <label htmlFor="editTenantBillingEmail" className={lbl}>Billing Email</label>
              <input id="editTenantBillingEmail" type="email" value={editBillingEmail} onChange={e => setEditBillingEmail(e.target.value)} placeholder="billing@example.com" disabled={isUpdating} className={inputCls} />
            </div>
            <div className="flex gap-3 justify-end pt-2">
              <button type="button" onClick={() => setEditingTenant(null)} disabled={isUpdating} className={secBtn}>Cancel</button>
              <button type="button" onClick={handleUpdateTenant} disabled={isUpdating} className={priBtn}>{isUpdating ? 'Updating…' : 'Update Tenant'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Tenant Modal */}
      {deletingTenant && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setDeletingTenant(null)}>
          <div className="w-full max-w-sm bg-white rounded-xl shadow-xl p-6 flex flex-col gap-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-semibold text-gray-900">Delete Tenant</h3>
            {deleteError && <div className="px-4 py-3 bg-error-light text-error border border-error/30 rounded-md text-label">{deleteError}</div>}
            <p className="text-label text-gray-700">Are you sure you want to delete <strong>{deletingTenant.name}</strong>?</p>
            <p className="text-label text-error">⚠️ This action cannot be undone. All data associated with this tenant will be permanently deleted.</p>
            <div className="flex gap-3 justify-end pt-2">
              <button type="button" onClick={() => setDeletingTenant(null)} disabled={isDeleting} className={secBtn}>Cancel</button>
              <button type="button" onClick={handleDeleteTenant} disabled={isDeleting} className={danBtn}>{isDeleting ? 'Deleting…' : 'Delete Tenant'}</button>
            </div>
          </div>
        </div>
      )}

      {/* User Management Modal */}
      {viewingUsersTenant && (
        <UserManagement tenantId={viewingUsersTenant.tenantId} tenantName={viewingUsersTenant.name}
          accessToken={accessToken} onClose={() => setViewingUsersTenant(null)} />
      )}
    </div>
  );
};

